import { useTranslation } from 'next-i18next';
import { contains } from 'ramda';
import { SyntheticEvent, useMemo } from 'react';
import { ISupplier } from '@bridebook/models/source/models/Suppliers.types';
import { PremiumTiers } from '@bridebook/models/source/models/Suppliers/Admin/admin.types';
import { useToggleBoolean } from '@bridebook/toolbox/src/utils/hooks/use-toggle-boolean';
import { Box, Collapse, CopyToClipboard, Divider, Text } from '@bridebook/ui';
import EmbeddedMap from '@bridebook/web/app-shared/components/embedded-map/embedded-map';
import ContentContainer from '@bridebook/web/app-basic/components/supplier/content-container';
import { LOCATION_SECTION_ID } from '@bridebook/web/app-shared/components/supplier/sections/ids';
import SupplierAccordion, {
  IAccordionProps,
} from '@bridebook/web/app-shared/components/supplier/sections/supplier-accordion/supplier-accordion';
import { IFair } from '@bridebook/web/app-shared/lib/supplier/types';
import { formatAddress } from '@bridebook/web/app-shared/lib/supplier/utils';
import ContactDetailsBar from '@bridebook/web/app-basic/components/supplier/sections/location-contact-details/contact-details-bar/contact-details-bar';
import Directions from '@bridebook/web/app-basic/components/supplier/sections/location-contact-details/directions';
import componentStyles from '@bridebook/web/app-basic/components/supplier/sections/location-contact-details/location-contact-details.style';
import { IShowPhoneProps } from '@bridebook/web/app-basic/components/supplier/sections/location-contact-details/contact-details-bar/show-phone';
import Social from '@bridebook/web/app-basic/components/supplier/sections/location-contact-details/social';

interface IProps {
  accordionProps: IAccordionProps;
  fairs: IFair[] | undefined;
  isLandscape?: boolean;
  isMobile: boolean;
  isTablet: boolean;
  enquiryFormButton: React.ReactNode;
  onEmailClick?: () => void;
  onMapInView?: () => void;
  onMapMouseUp?: () => void;
  onSocialMediaClick?: (title: string) => void;
  onWebsiteClick?: (e: SyntheticEvent<React.ReactNode>) => void;
  openStatic?: boolean;
  phoneProps: IShowPhoneProps;
  supplier: ISupplier;
  supplierTier?: PremiumTiers | number;
  isLoggedIn: boolean;
  noSocial?: boolean;
  isVenue?: boolean;
}

const LocationContactDetails = ({
  accordionProps,
  enquiryFormButton,
  fairs,
  isLandscape,
  isMobile,
  isTablet,
  onEmailClick,
  onMapInView,
  onMapMouseUp,
  onSocialMediaClick,
  onWebsiteClick,
  openStatic,
  phoneProps,
  supplier,
  isLoggedIn,
  isVenue,
  noSocial = false,
  supplierTier,
}: IProps) => {
  const { t } = useTranslation('supplier');
  const [detailsOpened, , , toggleDetailsOpened] = useToggleBoolean(false);

  const styles = componentStyles({ isLoggedIn });

  const leaveBorder =
    (isMobile || (isTablet && isLandscape)) &&
    contains('testimonials-accordion', accordionProps.accordions) &&
    fairs?.length === 0;

  const showMap = supplier.address && !supplier.flags?.hideStreet;
  const supplierName = supplier.name;

  const text = useMemo(() => {
    if (isLoggedIn) return t('locationContactDetails');
    if (isVenue) {
      return t('heading.locationContactDetailsWithName', {
        supplierName,
      });
    }

    return t('locationContactDetailsWithName', {
      supplierName,
    });
  }, [isLoggedIn, isVenue, supplierName, t]);

  const variant = useMemo(() => {
    if (!isLoggedIn && isVenue) return 'h2';
  }, [isLoggedIn, isVenue]);

  const formattedAddress = formatAddress(supplier) || '';

  return (
    <Box style={styles.wrapper} id={LOCATION_SECTION_ID}>
      <SupplierAccordion
        {...accordionProps}
        openStatic={openStatic}
        leaveBorder={leaveBorder}
        name="location-accordion"
        horizontalMargins={isMobile}
        text={text}
        variant={variant}>
        <ContentContainer>
          {isMobile && (
            <>
              <Text style={styles.addressTitle}>
                {isVenue ? t('contact.address.venue') : t('contact.address')}
              </Text>
              <Text>
                {formattedAddress}
                <CopyToClipboard style={styles.copyToClipboardButton} text={formattedAddress} />
              </Text>
            </>
          )}
          {showMap && (
            <Box style={styles.mapWrap}>
              <EmbeddedMap
                supplierName={supplier.name}
                supplierCity={supplier.address.city}
                supplierCountry={supplier.address.country}
                supplierPostalCode={supplier.address.postalCode}
                onMouseUp={onMapMouseUp}
                onMapInView={onMapInView}
              />
            </Box>
          )}
          <Box pt={showMap ? 0 : 6} style={styles.contactDetailsWrap}>
            <ContactDetailsBar
              supplier={supplier}
              supplierTier={supplierTier}
              phoneProps={phoneProps}
              onWebsiteClick={onWebsiteClick}
              onEmailClick={onEmailClick}
              isLoggedIn={isLoggedIn}
            />
          </Box>
          <Collapse isOpen={isMobile ? detailsOpened : true}>
            <Directions supplier={supplier} includeAddress={!isMobile} />
          </Collapse>
          {isMobile && (
            <Box as="a" style={styles.showDirectionsLink} onClick={toggleDetailsOpened}>
              {detailsOpened ? t('location.hideDirections') : t('location.showDirections')}
            </Box>
          )}
          {!noSocial && (
            <Box style={styles.socialWrap}>
              <Social supplier={supplier} onSocialMediaClick={onSocialMediaClick} />
            </Box>
          )}
          <Box style={styles.contactWrap}>{enquiryFormButton}</Box>
          {noSocial && <Divider spacingTop={6} spacingBottom={4} />}
        </ContentContainer>
      </SupplierAccordion>
    </Box>
  );
};

export default LocationContactDetails;
